// @ts-strict-ignore
import { clsx } from 'clsx';
import * as React from 'react';
import { formatPercent } from 'common/util';
import { withStyles } from 'common/util/react';
import { LENGTH, SIZE, TEXT_HIGHLIGHT_THEME, TextHighlightReact } from 'common/components/text-highlight';
import { TooltipReact } from 'common/components/tooltip';
import { DisplaySize } from 'common/components/tooltip/types';
import { LiftType } from 'common/report/util/lift/types';
import { LabelTheme } from 'common/components/items-menu/types';
import { ChartLegendTheme } from 'common/components/chart-legend/constants';
import { ChartLegendReact } from 'common/components/chart-legend';
import { MetricChartSize } from '../types';
import styles from './index.module.styl';
import css from './index.module.styl?inline';
export interface MetricLiftContext {
  liftType: LiftType;
  comparisonValue: string;
  liftComparisonValue: number;
}
interface MetricChartProps {
  comparisonValue?: number;
  label?: string;
  /** comma-separated path */
  metricId: string;
  value: number;
  /** formatted value to display */
  displayValue?: string;
  subLabel?: string;
  size?: MetricChartSize;
  color?: string;
  isCentered?: boolean;
  valueTooltip?: string;
  hideTooltip?: boolean;
  hideValue?: boolean;
  hideSubValue?: boolean;
  liftContext?: MetricLiftContext;
}
export const MetricChart: React.FC<MetricChartProps> = _ref => {
  let {
    comparisonValue,
    label,
    metricId,
    value,
    displayValue,
    subLabel,
    size = MetricChartSize.Large,
    color,
    isCentered,
    valueTooltip,
    hideTooltip,
    hideValue,
    hideSubValue,
    liftContext
  } = _ref;
  const renderMainValue = () => {
    let displayedValue = displayValue;
    let liftColor = null;
    if (liftContext) {
      const {
        liftType,
        liftComparisonValue
      } = liftContext;
      let addPositive = false;

      // overall isn't a comparison so it doesn't get + or coloring
      if (liftType !== LiftType.Overall) {
        if (liftComparisonValue !== 0) {
          liftColor = liftComparisonValue > 0 ? styles.positive : styles.negative;
        }
        if (liftComparisonValue > 0) {
          addPositive = true;
        }
      }
      displayedValue = "".concat(addPositive ? "+" : "").concat(formatPercent(liftComparisonValue));
    }
    return <div data-sentry-component="renderMainValue" data-sentry-source-file="index.tsx">
        {!hideTooltip && valueTooltip && <TooltipReact>{valueTooltip}</TooltipReact>}
        {!hideValue && <div className={clsx(styles.value, liftColor)} elref={"value"}>
            {displayedValue}
          </div>}
      </div>;
  };
  const renderLiftSubValue = () => {
    const {
      liftType,
      comparisonValue: liftComparisonValue
    } = liftContext;
    const segmentedValue = displayValue;
    const comparisonCopy = liftType !== LiftType.Overall ? " compared to " : " / ";
    return <>
        {!hideSubValue && <div>
            <TextHighlightReact theme={TEXT_HIGHLIGHT_THEME.NEUTRAL} text={segmentedValue} />
            {comparisonCopy}
            <TextHighlightReact theme={TEXT_HIGHLIGHT_THEME.NEUTRAL} text={liftComparisonValue} />
          </div>}
        {subLabel && <div className={styles.subLabel} elref={"subLabel"}>
            {subLabel}
          </div>}
      </>;
  };
  const renderComparison = () => {
    const percentChange = (value - comparisonValue) / Math.abs(comparisonValue);
    const percentChangePositive = percentChange > 0;
    return <>
        {subLabel && <div className={styles.subLabel} elref={"subLabel"}>
            {subLabel}
          </div>}
        {!hideSubValue && <div className={styles.changeRow}>
            <TextHighlightReact elref={"comparisonValue"} text={formatPercent(Math.abs(percentChange))} {...isFinite(percentChange) ? {
          icon: percentChangePositive ? "arrow-up" : "arrow-down",
          theme: percentChangePositive ? TEXT_HIGHLIGHT_THEME.POSITIVE : TEXT_HIGHLIGHT_THEME.NEGATIVE
        } : {}} size={SIZE.SMALL} length={LENGTH.WIDE} />
          </div>}
      </>;
  };
  const renderSubValue = () => {
    if (liftContext) {
      return renderLiftSubValue();
    } else if (comparisonValue) {
      // time comparison
      return renderComparison();
    }
    return <>
        {subLabel && <div className={styles.subLabel} elref={"subLabel"}>
            {subLabel}
          </div>}
      </>;
  };
  const legendItems = [{
    label,
    value: metricId,
    color,
    isSelected: true,
    labelTheme: LabelTheme.Soft,
    wrapLabel: true
  }];
  return <div className={clsx(styles.root, {
    [styles.sizeSmall]: size === MetricChartSize.Small,
    [styles.sizeMedium]: size === MetricChartSize.Medium,
    [styles.centered]: isCentered
  })} data-sentry-component="MetricChart" data-sentry-source-file="index.tsx">
      <div className={styles.segmentNameContainer}>
        <ChartLegendReact maxTableItemWidth={"100%"} sections={[{
        items: legendItems
      }]} theme={ChartLegendTheme.CardTable} justifyStart={!isCentered} className={styles.legend} data-sentry-element="ChartLegendReact" data-sentry-source-file="index.tsx" />
        {!hideTooltip && <TooltipReact size={DisplaySize.Medium}>{label}</TooltipReact>}
      </div>
      {renderMainValue()}
      {renderSubValue()}
    </div>;
};
export const MetricChartReact = withStyles(MetricChart, css);